/**
 * Word Hybrid Edit Service
 * Orchestrates edit application using a hybrid batch-sequential approach
 */

/* global Word */

import {
  EditApplicationResult,
  AutoRedlineOutput,
  EditOperation,
  EditGroup,
} from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";
import {
  clearAppliedParagraphLedger,
  needsAppliedTextBaseline,
  recordAppliedParagraphText,
} from "./appliedParagraphLedger";
import { ParagraphApplyContext } from "./applyContext";
import { applyGroups } from "./editGroupRunner";
import { UnifiedIndexTracker } from "./wordIndexAdjustmentTracker";
import { resolveListChange } from "./listChangeHelpers";

const log = createLogger("WordHybridEditService");

/**
 * CS-259: Tracker cache to persist index tracking across multiple edit applications
 *
 * For INSERT/DELETE operations: Uses document-level key "__document_index_tracker__"
 * because these operations shift paragraph indices across the entire document.
 *
 * Lifecycle: Tracker persists across multiple "Apply" button clicks until cleared
 * Used for both INSERT and DELETE operations to track index adjustments
 */
const trackerCache = new Map<string, UnifiedIndexTracker>();

/**
 * Constant key for document-level index tracking (INSERT/DELETE operations)
 */
const DOCUMENT_TRACKER_KEY = "__document_index_tracker__";

/**
 * CS-259: Clears the document-level apply state — the index tracker and the
 * self-attribution ledger (see appliedParagraphLedger.ts).
 *
 * Call this when starting a new chat response or when resetting the UI.
 * Both describe "what we have applied, or are about to apply, since the last
 * user message", so they share one lifetime and always clear together.
 */
export function clearDocumentIndexTracker(): void {
  if (trackerCache.has(DOCUMENT_TRACKER_KEY)) {
    const tracker = trackerCache.get(DOCUMENT_TRACKER_KEY);
    const count = tracker?.getOperationCount() ?? 0;
    trackerCache.delete(DOCUMENT_TRACKER_KEY);
    log.debug(`CS-259: Cleared document-level index tracker (${count} operations tracked)`);
  }
  clearAppliedParagraphLedger();
}

/**
 * CS-259: Clears the index tracker for a specific section
 * @deprecated Use clearDocumentIndexTracker for INSERT/DELETE operations
 */
export function clearIndexTracker(sectionName: string): void {
  // Also clear the document-level tracker for backward compatibility
  clearDocumentIndexTracker();

  if (trackerCache.has(sectionName)) {
    trackerCache.delete(sectionName);
    log.debug(`CS-259: Cleared index tracker for section "${sectionName}"`);
  }
}

/**
 * CS-259: Clears the insertion tracker for a specific section
 * @deprecated Use clearDocumentIndexTracker instead
 */
export function clearInsertionTracker(_sectionName: string): void {
  clearDocumentIndexTracker();
}

/**
 * CS-259: Clears all index trackers, plus the self-attribution ledger
 * (same lifetime — see clearDocumentIndexTracker).
 * Call this when starting a new chat session
 */
export function clearAllIndexTrackers(): void {
  const count = trackerCache.size;
  trackerCache.clear();
  clearAppliedParagraphLedger();
  if (count > 0) {
    log.debug(`CS-259: Cleared ${count} index trackers`);
  }
}

/**
 * CS-259: Clears all insertion trackers
 * @deprecated Use clearAllIndexTrackers instead
 */
export function clearAllInsertionTrackers(): void {
  clearAllIndexTrackers();
}

/**
 * Read the live text of a paragraph by its backend-emitted paragraphId, using
 * the document-level UnifiedIndexTracker to account for any INSERT/DELETE
 * operations CS has already applied in this session.
 *
 * Used by the stale-paragraph detection in AutoRedlinePreview before edits run.
 * Returns outOfBounds=true when the adjusted index falls outside the document
 * (treat as stale at the caller — the paragraph the BE generated against no
 * longer exists at the expected position).
 */
export async function readLiveParagraphText(
  paragraphId: number
): Promise<{ text: string; outOfBounds: boolean; adjustedIndex: number }> {
  const tracker = trackerCache.get(DOCUMENT_TRACKER_KEY);
  const adjustedIndex = tracker ? tracker.calculateAdjustedIndexForEdit(paragraphId) : paragraphId;

  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    if (adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length) {
      return { text: "", outOfBounds: true, adjustedIndex };
    }

    const paragraph = paragraphs.items[adjustedIndex];
    paragraph.load("text");
    await context.sync();
    return { text: paragraph.text, outOfBounds: false, adjustedIndex };
  });
}

/**
 * Groups edits by their sync requirements
 * Consecutive edits without requiresSync flag are grouped together
 */
function groupEditsBySyncRequirement(edits: EditOperation[]): EditGroup[] {
  // Log all requiresSync values to verify backend data is preserved
  log.debug("Grouping edits by sync requirement", {
    totalEdits: edits.length,
  });

  const groups: EditGroup[] = [];
  let currentBatch: EditOperation[] = [];

  for (const edit of edits) {
    // List-structure edits issue their own context.sync() calls, so they must not
    // share the batch's deferred single sync — isolate them like requiresSync edits.
    if (edit.requiresSync || resolveListChange(edit) !== undefined) {
      // Flush current batch if any
      if (currentBatch.length > 0) {
        groups.push({ isBatch: true, edits: currentBatch });
        currentBatch = [];
      }
      // Add single edit that requires its own sync boundary
      groups.push({ isBatch: false, edits: [edit] });
      log.debug(`Edit ${edit.editId} isolated into a single group (requiresSync or listChange)`);
    } else {
      // Add to current batch
      currentBatch.push(edit);
      log.debug(`Edit ${edit.editId} has requiresSync=${edit.requiresSync}, adding to batch`);
    }
  }

  // Flush remaining batch
  if (currentBatch.length > 0) {
    groups.push({ isBatch: true, edits: currentBatch });
  }

  // Log the final groups for verification
  log.debug("Edit groups created", {
    totalGroups: groups.length,
    batchGroups: groups.filter((g) => g.isBatch).length,
    singleGroups: groups.filter((g) => !g.isBatch).length,
  });

  return groups;
}

/**
 * Calculates total sync calls needed for the grouped edits
 */
function calculateTotalSyncs(groups: EditGroup[]): number {
  let syncs = 1; // Initial sync for searchParagraph

  for (const group of groups) {
    if (group.isBatch && group.edits.length > 1) {
      // Batch: 1-2 syncs for searches, 1 for applications
      syncs += group.edits.some((e) => e.position === "replace" && e.targetText) ? 3 : 2;
    } else {
      // Single edit: 1-2 syncs for search, 1 for apply
      const edit = group.edits[0];
      syncs += edit.position === "replace" && edit.targetText ? 3 : 2;
    }
  }

  syncs += 1; // Final cleanup sync
  return syncs;
}

/**
 * Apply edits to a document using a hybrid batch-sequential approach
 * Groups consecutive edits without requiresSync flag for batch application
 * This optimizes performance by reducing sync calls while maintaining correctness:
 * 1. Batch edits that don't have dependencies (requiresSync: false)
 * 2. Apply edits with dependencies individually (requiresSync: true)
 * 3. Maintains reverse order application within each group
 * 4. Uses document-wide search for each edit (no searchParagraph dependency)
 *
 * IMPORTANT: Each edit performs document-wide search for its searchPhrase (<250 chars)
 * and selects the correct occurrence using searchPhraseOccurrence from backend.
 * This eliminates the 256-character limit issues on Word Desktop.
 */
export async function applyEditsSequentially(
  autoRedline: AutoRedlineOutput,
  editIds?: string[],
  messageId?: string
): Promise<EditApplicationResult[]> {
  const startTime = Date.now();

  // Declared OUTSIDE Word.run so the catch below can still see what applied. A
  // throw escaping the callback used to discard every recorded outcome and mark
  // the whole output failed, including edits already in the document.
  const results: EditApplicationResult[] = [];

  try {
    return await Word.run(async (context) => {
      // Extract operationType from AutoRedlineOutput (defaults to 'edit')
      const operationType = autoRedline.operationType ?? "edit";

      // Extract paragraphId from AutoRedlineOutput (all edits in section target this paragraph)
      const paragraphId = autoRedline.paragraphId;

      // Filter and reverse edits
      let editsToApply = editIds
        ? autoRedline.edits.filter((e) => editIds.includes(e.editId))
        : autoRedline.edits;

      // IMPORTANT: Apply edits in reverse order to maintain position integrity
      editsToApply = [...editsToApply].reverse();

      // Step 2: Group edits by sync requirements
      const groups = groupEditsBySyncRequirement(editsToApply);

      // Calculate sync optimization metrics
      const totalSyncs = calculateTotalSyncs(groups);
      const sequentialSyncs = editsToApply.length * 2;

      // CS-259: Extract trackChangesMode for passing to record methods
      // This ensures each operation records the correct track changes state
      const trackChangesMode = autoRedline.trackChangesMode ?? "off";

      // CS-259: Get or create unified index tracker for operations that need paragraph adjustment
      // IMPORTANT: Use document-level tracker (not section-specific) because INSERT/DELETE
      // operations shift paragraph indices across the entire document
      // EDIT operations need to READ from the tracker to adjust their paragraphId
      let indexTracker: UnifiedIndexTracker | undefined;
      const cacheKey = DOCUMENT_TRACKER_KEY;

      if (operationType === "insert" || operationType === "delete") {
        // INSERT/DELETE: Create tracker if doesn't exist, then record operations
        if (!trackerCache.has(cacheKey)) {
          trackerCache.set(cacheKey, new UnifiedIndexTracker(trackChangesMode));
          log.debug("CS-259: Created new document-level UnifiedIndexTracker", {
            section: autoRedline.sectionName,
            operationType,
            trackChangesMode,
          });
        } else {
          log.debug("CS-259: Using existing document-level UnifiedIndexTracker", {
            section: autoRedline.sectionName,
            operationType,
            trackChangesMode,
            trackedOperations: trackerCache.get(cacheKey)!.getOperationCount(),
          });
        }
        indexTracker = trackerCache.get(cacheKey);
      } else if (operationType === "edit" && paragraphId !== undefined) {
        // EDIT with paragraphId: Get existing tracker if available (read-only access)
        // EDIT operations don't record to tracker, but need to read adjustments from prior INSERT/DELETE
        if (trackerCache.has(cacheKey)) {
          indexTracker = trackerCache.get(cacheKey);
          log.info("CS-259: EDIT operation using existing index tracker for paragraph adjustment", {
            section: autoRedline.sectionName,
            paragraphId,
            trackedOperations: indexTracker!.getOperationCount(),
          });
        } else {
          log.debug("CS-259: EDIT operation has no tracker (no prior INSERT/DELETE operations)", {
            section: autoRedline.sectionName,
            paragraphId,
          });
        }
      }

      log.debug("Applying edits using hybrid approach", {
        section: autoRedline.sectionName,
        operationType,
        totalEdits: editsToApply.length,
        groups: groups.length,
      });

      // Built once and shared by every group — the appliers only read from it.
      const apply: ParagraphApplyContext = {
        paragraphId,
        indexTracker,
        trackChangesMode,
        expectedParagraphText: autoRedline.searchParagraph,
        messageId,
      };

      // Step 3: Apply each group
      await applyGroups(groups, operationType, context, startTime, apply, results);

      log.info("Edit application completed", {
        success: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).length,
      });

      // Self-attribution (see appliedParagraphLedger.ts). EDIT is the only
      // operation that rewrites the anchor text in place, so it is the only one
      // that captures. Not gated on success: if nothing changed the entry just
      // duplicates the BE snapshot and is inert.
      //
      // Kept in its own try/catch so a ledger failure stays a ledger failure and
      // never surfaces as an apply error. Failing to capture only costs a
      // sibling card a stale block.
      if (
        operationType === "edit" &&
        paragraphId !== undefined &&
        messageId &&
        needsAppliedTextBaseline(messageId, paragraphId)
      ) {
        try {
          await recordAppliedParagraphText(context, { messageId, paragraphId, indexTracker });
        } catch (ledgerError) {
          log.warn("Could not record applied paragraph text; sibling cards may block as stale", {
            paragraphId,
            error: ledgerError instanceof Error ? ledgerError.message : String(ledgerError),
          });
        }
      }

      // Reverse results back to match original order
      return results.reverse();
    });
  } catch (error) {
    log.error("Sequential edit application failed", { section: autoRedline.sectionName, error });

    const errorMsg = error instanceof Error ? error.message : "Sequential operation failed";
    const editsToApply = editIds
      ? autoRedline.edits.filter((e) => editIds.includes(e.editId))
      : autoRedline.edits;

    // Keep the real outcome for every edit that already produced one — those
    // were genuinely applied (or genuinely failed) and are reflected in the
    // document. Only edits we never reached are reported as failed here.
    const recorded = new Map(results.map((result) => [result.editId, result]));

    return editsToApply.map(
      (edit) =>
        recorded.get(edit.editId) ?? {
          success: false,
          editId: edit.editId,
          error: errorMsg,
          duration: Date.now() - startTime,
        }
    );
  }
}
